import React from 'react';
import { Modal, Button } from 'components';
import { HomeService } from 'services';
import moment from 'moment';

interface Props {
	visible: boolean;
	title: string;
	type: string;
	since?: string;
	until?: string;
	onClose: () => void;
}

interface UserRow {
	id?: number | string;
	name?: string;
	lastname?: string;
	email?: string;
	created_at?: string;
	has_active_plan?: boolean;
	plan_name?: string;
}

interface State {
	loading: boolean;
	users: UserRow[];
	count: number;
	error: boolean;
}

class ModalUsersDetail extends React.Component<Props, State> {

	state: State = {
		loading: false,
		users: [],
		count: 0,
		error: false,
	}

	componentDidUpdate(prevProps: Props) {
		// Cargar al abrir, o si cambia el type / rango mientras está abierto
		const opened = this.props.visible && !prevProps.visible;
		const changed = this.props.visible && (
			prevProps.type !== this.props.type ||
			prevProps.since !== this.props.since ||
			prevProps.until !== this.props.until
		);
		if (opened || changed) {
			this.load();
		}
	}

	load = async () => {
		this.setState({ loading: true, error: false });
		try {
			const res: any = await HomeService.admin.getUsersDetail({
				type: this.props.type,
				since: this.props.since,
				until: this.props.until,
				withoutLoading: true,
			}) || {};
			const users = Array.isArray(res.users) ? res.users : [];
			this.setState({
				users,
				count: typeof res.count === 'number' ? res.count : users.length,
				loading: false,
			});
		}
		catch (e) {
			this.setState({ loading: false, error: true, users: [], count: 0 });
		}
	}

	fullName = (u: UserRow) => {
		const name = [u?.name, u?.lastname].filter(Boolean).join(' ').trim();
		return name || '—';
	}

	exportCSV = () => {
		const { users } = this.state;
		if (!users.length) return;

		const header = ['Nombre', 'Email', 'Registro', 'Plan'];
		const rows = users.map((u) => ([
			this.fullName(u),
			u?.email || '',
			u?.created_at ? moment(u.created_at).format('DD/MM/YYYY HH:mm') : '',
			u?.has_active_plan ? (u?.plan_name || 'Premium') : 'Free',
		]));

		// Escapado CSV básico (comillas y comas)
		const escape = (v: any) => {
			const s = String(v ?? '');
			return /[",\n]/.test(s) ? `"${ s.replace(/"/g, '""') }"` : s;
		}
		const csv = [header, ...rows]
			.map((r) => r.map(escape).join(','))
			.join('\n');

		const blob = new Blob([`﻿${ csv }`], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `usuarios-${ this.props.type }-${ moment().format('YYYYMMDD-HHmm') }.csv`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}

	render() {
		const { visible, title, onClose } = this.props;
		const { loading, users, count, error } = this.state;

		return (
			<Modal
				className="modal-users-detail"
				visible={ visible }
				title={ title }
				onClose={ onClose }
			>
				<div id="modal-users-detail">
					<div className="muds-summary">
						{
							loading ? (
								<span className="muds-summary-skeleton" />
							) : error ? (
								<span className="muds-error">No se pudieron cargar los usuarios</span>
							) : (
								<span>
									<strong>{ count }</strong> { count === 1 ? 'usuario encontrado' : 'usuarios encontrados' }
								</span>
							)
						}
					</div>

					<div className="muds-table-wrap">
						<table className="table">
							<thead>
								<tr>
									<th>Nombre</th>
									<th>Email</th>
									<th>Registro</th>
									<th className="text-right">Plan</th>
								</tr>
							</thead>
							<tbody>
								{
									loading && (
										[0, 1, 2, 3, 4].map((i) => (
											<tr key={ `sk-${ i }` } className="muds-skeleton-row">
												<td><span className="muds-cell-skeleton" /></td>
												<td><span className="muds-cell-skeleton" /></td>
												<td><span className="muds-cell-skeleton" /></td>
												<td className="text-right"><span className="muds-cell-skeleton sm" /></td>
											</tr>
										))
									)
								}
								{
									!loading && users.map((u, index) => (
										<tr key={ u?.id || index }>
											<td className="ellipsis">{ this.fullName(u) }</td>
											<td className="ellipsis email">{ u?.email || '—' }</td>
											<td className="reg">
												{ u?.created_at ? moment(u.created_at).format('DD/MM/YYYY HH:mm') : '—' }
											</td>
											<td className="text-right">
												{
													u?.has_active_plan ? (
														<span className="badge-premium">{ u?.plan_name || 'Premium' }</span>
													) : (
														<span className="badge-free">Free</span>
													)
												}
											</td>
										</tr>
									))
								}
								{
									!loading && !error && users.length === 0 && (
										<tr>
											<td colSpan={ 4 } className="no-items">No se encontraron usuarios</td>
										</tr>
									)
								}
							</tbody>
						</table>
					</div>

					<div className="muds-footer">
						<Button className="btn-close-muds" color="secondary" onClick={ onClose }>
							Cerrar
						</Button>
						<Button
							className="btn-export-muds"
							disabled={ loading || users.length === 0 }
							onClick={ this.exportCSV }>
							Exportar a CSV
						</Button>
					</div>
				</div>
			</Modal>
		)
	}
}

export default ModalUsersDetail;
